import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Target, 
  MessageSquare,
  FileText,
  Download,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  UserCheck,
  BookOpen,
  Settings,
  Clock,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  X,
  Sparkles,
  Zap,
  RefreshCw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line
} from 'recharts';
import { ReportPreview } from '@/components/ReportPreview';
import {
  formatScore,
  SegmentBreakdownChart,
  DriverScoreGauge,
  KeyMetricsTile,
  TrendAnalysisChart,
  AIInsightsTile,
  QuestionSummaryCard,
} from '@/components/dashboard/DashboardWidgets';

// Color palette
const COLORS = ['#5AA2AE', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#ec4899', '#06b6d4'];

// SegmentBreakdownChart — imported from @/components/dashboard/DashboardWidgets

// DriverScoreGauge — imported from @/components/dashboard/DashboardWidgets

// KeyMetricsTile — imported from @/components/dashboard/DashboardWidgets


// TrendAnalysisChart — imported from @/components/dashboard/DashboardWidgets


// AIInsightsTile — imported from @/components/dashboard/DashboardWidgets

// QuestionSummaryCard — imported from @/components/dashboard/DashboardWidgets

// Question Detail Modal Content
function QuestionDetailModal({ question, segmentTypes, onClose }) {
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiSummary, setAISummary] = useState(null);
  
  // Update aiSummary when question changes
  useEffect(() => {
    if (question?.ai_summary) {
      setAISummary(question.ai_summary);
    }
  }, [question]);
  
  if (!question) return null;
  
  const COLORS = ['#5AA2AE', '#9b87f5', '#22c55e', '#f59e0b', '#ef4444', '#6366f1'];
  
  // Prepare segment breakdown data
  const segmentData = question.by_segment || [];
  
  // Sort segments by score to find highest and lowest
  const sortedSegments = [...segmentData].sort((a, b) => (b.avg || 0) - (a.avg || 0));
  const highestSegment = sortedSegments[0];
  const lowestSegment = sortedSegments[sortedSegments.length - 1];
  
  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-4 bg-slate-50 rounded-lg">
          <p className="text-2xl font-bold text-[#5AA2AE]">{question.response_count}</p>
          <p className="text-xs text-slate-500">Total Responses</p>
        </div>
        {question.avg !== undefined && (
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold text-emerald-600">{question.avg}</p>
            <p className="text-xs text-slate-500">Average Score</p>
          </div>
        )}
        {question.std_dev !== undefined && (
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold text-slate-600">{question.std_dev}</p>
            <p className="text-xs text-slate-500">Std Deviation</p>
          </div>
        )}
      </div>
      
      {/* Highest/Lowest Scoring Segments - for numeric questions */}
      {question.question_type === 'slider' && segmentData.length >= 2 && highestSegment && lowestSegment && (
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">Highest Scoring</span>
            </div>
            <p className="text-sm font-semibold text-emerald-800">{highestSegment.segment_name}</p>
            <p className="text-lg font-bold text-emerald-600">{highestSegment.avg} <span className="text-xs font-normal">avg</span></p>
            <p className="text-xs text-emerald-600">n={highestSegment.response_count}</p>
          </div>
          <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-rose-600" />
              <span className="text-xs font-medium text-rose-700">Lowest Scoring</span>
            </div>
            <p className="text-sm font-semibold text-rose-800">{lowestSegment.segment_name}</p>
            <p className="text-lg font-bold text-rose-600">{lowestSegment.avg} <span className="text-xs font-normal">avg</span></p>
            <p className="text-xs text-rose-600">n={lowestSegment.response_count}</p>
          </div>
        </div>
      )}
      
      {/* Visualization based on question type */}
      {question.question_type === 'slider' && question.distribution && (
        <div>
          <h4 className="text-sm font-medium mb-3">Response Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={question.distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="value" />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="count" fill="#5AA2AE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {question.question_type === 'multi_topic' && question.top_topics && (
        <div>
          <h4 className="text-sm font-medium mb-3">Topic Breakdown</h4>
          <div className="space-y-2">
            {question.top_topics.map((topic, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{topic.name}</span>
                    <span className="text-sm text-slate-500">{topic.count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#5AA2AE] rounded-full"
                      style={{ width: `${(topic.count / question.response_count) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Free Text - AI Summary + Sample Responses */}
      {question.question_type === 'free_text' && (
        <div className="space-y-4">
          {/* AI Theme Analysis */}
          {(aiSummary || question.theme_summary) && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-purple-600" />
                <h4 className="text-sm font-medium text-purple-700">AI Theme Analysis</h4>
              </div>
              <p className="text-sm text-purple-800">{aiSummary || question.theme_summary}</p>
            </div>
          )}
          
          {/* Sentiment Breakdown */}
          {question.sentiment_breakdown && (
            <div>
              <h4 className="text-sm font-medium mb-2">Sentiment Breakdown</h4>
              <div className="flex gap-2">
                <div className="flex-1 p-2 bg-emerald-50 rounded text-center">
                  <p className="text-lg font-bold text-emerald-600">{question.sentiment_breakdown.positive || 0}%</p>
                  <p className="text-xs text-emerald-700">Positive</p>
                </div>
                <div className="flex-1 p-2 bg-slate-50 rounded text-center">
                  <p className="text-lg font-bold text-slate-600">{question.sentiment_breakdown.neutral || 0}%</p>
                  <p className="text-xs text-slate-700">Neutral</p>
                </div>
                <div className="flex-1 p-2 bg-rose-50 rounded text-center">
                  <p className="text-lg font-bold text-rose-600">{question.sentiment_breakdown.negative || 0}%</p>
                  <p className="text-xs text-rose-700">Concerned</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Sample Responses */}
          {question.sample_responses && question.sample_responses.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-3">Sample Responses</h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {question.sample_responses.slice(0, 5).map((response, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 italic">
                    "{response}"
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Full Segment Analysis */}
      {segmentData.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-3">Analysis by Segment</h4>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {sortedSegments.map((seg, i) => {
              const isHighest = i === 0 && segmentData.length > 1;
              const isLowest = i === sortedSegments.length - 1 && segmentData.length > 1;
              return (
                <div 
                  key={i} 
                  className={`flex items-center justify-between p-2 rounded ${
                    isHighest ? 'bg-emerald-50 border border-emerald-200' : 
                    isLowest ? 'bg-rose-50 border border-rose-200' : 
                    'bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isHighest && <TrendingUp className="w-3 h-3 text-emerald-600" />}
                    {isLowest && <TrendingDown className="w-3 h-3 text-rose-600" />}
                    <span className="text-sm">{seg.segment_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${isHighest ? 'text-emerald-600' : isLowest ? 'text-rose-600' : ''}`}>
                      {seg.avg || seg.count}
                    </span>
                    <span className="text-xs text-slate-400">n={seg.response_count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Trend Analysis */}
      {question.trend ? (
        <div className={`p-4 rounded-lg ${question.trend.change > 0 ? 'bg-emerald-50' : question.trend.change < 0 ? 'bg-rose-50' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-2">
            {question.trend.change > 0 ? (
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            ) : question.trend.change < 0 ? (
              <TrendingDown className="w-4 h-4 text-rose-600" />
            ) : (
              <TrendingUp className="w-4 h-4 text-slate-400" />
            )}
            <span className={`text-sm font-medium ${question.trend.change > 0 ? 'text-emerald-700' : question.trend.change < 0 ? 'text-rose-700' : 'text-slate-600'}`}>
              {question.trend.change > 0 ? '+' : ''}{question.trend.change}% since last period
            </span>
          </div>
          {question.trend.previous_avg && (
            <p className="text-xs text-slate-500 mt-1">Previous average: {question.trend.previous_avg}</p>
          )}
        </div>
      ) : (
        <div className="p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2 text-slate-500">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Trend analysis available after multiple survey periods</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectSummaryPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, isProjectAdmin, segmentTypes } = useProject();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);

  // New unified report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportPhase, setReportPhase] = useState('confirm'); // confirm | generating | ready
  const [reportSteps, setReportSteps] = useState([]);
  const [reportError, setReportError] = useState(null);
  const reportDataRef = useRef(null);
  const pregenPdfTaskRef = useRef(null);
  const [stakeholderSectionOpen, setStakeholderSectionOpen] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  // New state for key insights and segment filtering
  const [keyInsights, setKeyInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [filteredDrivers, setFilteredDrivers] = useState(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  
  // Check if current user is admin
  const isAdmin = isProjectAdmin || currentProject?.is_admin || currentProject?.is_owner;
  
  // Fetch summary data and dashboard config
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [summaryRes, configRes] = await Promise.all([
          api.get(`/projects/${projectId}/executive-summary`),
          api.get(`/projects/${projectId}/dashboard-config`).catch(() => ({ data: null }))
        ]);
        setSummaryData(summaryRes.data);
        setDashboardConfig(configRes.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching summary:', err);
        setError(err.response?.data?.detail || 'Failed to load summary');
      } finally {
        setLoading(false);
      }
    };
    
    if (projectId) {
      fetchData();
    }
  }, [projectId]);
  
  // Fetch key insights separately (can be slow due to AI)
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoadingInsights(true);
        const response = await api.get(`/projects/${projectId}/key-insights`);
        setKeyInsights(response.data);
      } catch (err) {
        console.error('Error fetching key insights:', err);
        // Don't set error - this is non-critical
      } finally {
        setLoadingInsights(false);
      }
    };
    
    if (projectId) {
      fetchInsights();
    }
  }, [projectId]);
  
  // Handle segment click for filtering
  const handleSegmentClick = (segment) => {
    if (selectedSegment?.segment_id === segment.segment_id) {
      // Clicking same segment clears filter
      setSelectedSegment(null);
      setFilteredDrivers(null);
    } else {
      // Include the completed count to check anonymity threshold
      setSelectedSegment({
        ...segment,
        meetsThreshold: (segment.completed || 0) >= (currentProject?.anonymity_threshold || 3)
      });
    }
  };

  // Fetch filtered driver scores when segment changes
  useEffect(() => {
    if (!selectedSegment || !selectedSegment.meetsThreshold) {
      setFilteredDrivers(null);
      return;
    }
    const fetchFilteredDrivers = async () => {
      setLoadingDrivers(true);
      try {
        const res = await api.get(`/projects/${projectId}/driver-scores?segment_id=${selectedSegment.segment_id}`);
        setFilteredDrivers(res.data.driver_summaries);
      } catch (err) {
        console.error('Error fetching filtered drivers:', err);
        setFilteredDrivers(null);
      } finally {
        setLoadingDrivers(false);
      }
    };
    fetchFilteredDrivers();
  }, [selectedSegment, projectId]);
  
  // Check if selected segment meets anonymity threshold
  const selectedSegmentMeetsThreshold = !selectedSegment || selectedSegment.meetsThreshold;
  
  // Refresh key insights
  const refreshInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await api.get(`/projects/${projectId}/key-insights`);
      setKeyInsights(response.data);
    } catch (err) {
      console.error('Error refreshing insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };
  
  // Unified report generation — collects all data including AI before showing anything
  const generateFullReport = async () => {
    setReportPhase('generating');
    setReportError(null);

    const updateStep = (id, status, detail) => {
      setReportSteps(prev => {
        const idx = prev.findIndex(s => s.id === id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], status, detail: detail || copy[idx].detail };
          return copy;
        }
        return [...prev, { id, label: detail || id, status, detail: detail || id }];
      });
    };

    // Define steps
    setReportSteps([
      { id: 'project', label: 'Fetching project data', status: 'pending' },
      { id: 'summary', label: 'Building executive summary', status: 'pending' },
      { id: 'trends', label: 'Loading trend analysis', status: 'pending' },
      { id: 'ai_drivers', label: 'AI driver analysis', status: 'pending' },
      { id: 'ai_segments', label: 'AI segment analysis', status: 'pending' },
      { id: 'ai_themes', label: 'AI text themes', status: 'pending' },
    ]);

    try {
      // Phase 1: Fast parallel calls
      updateStep('project', 'active', 'Fetching project data...');
      const [reportDataRes, summaryRes, insightsRes, publicStatsRes, overallTrendRes] = await Promise.all([
        api.get(`/projects/${projectId}/report-preview-data`),
        api.get(`/projects/${projectId}/executive-summary`),
        api.get(`/projects/${projectId}/key-insights`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/stakeholders/public-stats`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/trend-analysis`).catch(() => ({ data: null })),
      ]);
      const data = reportDataRes.data;
      const dashData = summaryRes.data;
      updateStep('project', 'done', 'Project data loaded');

      updateStep('summary', 'active', 'Building executive summary...');
      const segmentTypes = dashData?.segments_by_type || [];
      const segTrends = await Promise.all(
        segmentTypes.map(st =>
          api.get(`/projects/${projectId}/trend-analysis?segment_type_id=${st.id}`)
            .then(res => ({ id: st.id, name: st.name, data: res.data }))
            .catch(() => ({ id: st.id, name: st.name, data: null }))
        )
      );

      const dateStr = data.generated_at ? new Date(data.generated_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }) : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

      data._dateFormatted = dateStr;
      data._dashSegmentsByType = segmentTypes;
      data._dashDriverSummaries = dashData?.driver_summaries || [];
      data._dashQuestionSummaries = dashData?.question_summaries || [];
      data._dashParticipation = dashData?.participation || {};
      data._dashOverallSentiment = dashData?.overall_sentiment;
      data._insights = insightsRes?.data || keyInsights || null;
      data._publicStats = publicStatsRes?.data;
      data._overallTrend = overallTrendRes?.data;
      data._segmentTrends = segTrends;
      data._segmentAnalysis = [];
      updateStep('summary', 'done', 'Executive summary ready');

      updateStep('trends', 'done', 'Trend analysis loaded');

      // Phase 2: AI calls — sequential per-driver to avoid gateway timeout
      updateStep('ai_drivers', 'active', 'Generating AI driver analysis...');
      updateStep('ai_segments', 'active', 'Generating AI segment analysis...');
      updateStep('ai_themes', 'active', 'Generating AI text themes...');

      // Driver insights — call per-driver sequentially (each ~15s, avoids gateway timeout)
      const driverInsights = [...(data.driver_insights || [])];
      const drivers = driverInsights.map(d => ({ id: d.driver_id, name: d.driver }));
      let driversDone = 0;
      for (const drv of drivers) {
        try {
          updateStep('ai_drivers', 'active', `AI analysis: ${drv.name} (${driversDone + 1}/${drivers.length})...`);
          const res = await api.post(`/projects/${projectId}/driver-report-insight/${drv.id}`);
          const ai = res.data;
          if (ai?.insight) {
            const idx = driverInsights.findIndex(d => d.driver_id === ai.driver_id || d.driver === ai.driver_name);
            if (idx >= 0) driverInsights[idx] = { ...driverInsights[idx], insight: ai.insight };
          }
        } catch (err) {
          console.warn(`Driver insight failed for ${drv.name}:`, err);
        }
        driversDone++;
      }
      data.driver_insights = driverInsights;
      updateStep('ai_drivers', 'done', `AI driver analysis complete (${driversDone}/${drivers.length})`);

      // Segment analysis + Free text themes — run in parallel (each call is per-type, manageable)
      const aiPromises = [];

      // Segment analysis — per segment type
      const segTypeGroups = data.segment_type_groups || [];
      const segPromises = segTypeGroups.map(stg => {
        if (!stg.type_id) return Promise.resolve();
        return api.post(`/projects/${projectId}/segment-analysis?segment_type_id=${stg.type_id}`)
          .then(res => {
            const newResults = res.data || [];
            data._segmentAnalysis = [...(data._segmentAnalysis || []), ...newResults];
          })
          .catch(() => {});
      });
      aiPromises.push(
        Promise.all(segPromises).then(() => {
          updateStep('ai_segments', 'done', 'AI segment analysis complete');
        })
      );

      // Free text themes
      aiPromises.push(
        api.post(`/projects/${projectId}/free-text-themes`)
          .then(res => {
            const aiThemes = res.data || [];
            if (aiThemes.length) {
              const driverInsights = [...(data.driver_insights || [])];
              driverInsights.forEach((driver, di) => {
                const questions = [...(driver.questions || [])];
                questions.forEach((q, qi) => {
                  if (q.question_type === 'free_text') {
                    const match = aiThemes.find(a => a.question_id === q.id);
                    if (match) questions[qi] = { ...q, sentiment_breakdown: match.sentiment_breakdown, themes: match.themes };
                  }
                });
                driverInsights[di] = { ...driver, questions };
              });
              data.driver_insights = driverInsights;
              const publicQs = [...(data.public_questions || [])];
              publicQs.forEach((q, qi) => {
                if (q.question_type === 'free_text') {
                  const match = aiThemes.find(a => a.question_id === q.id);
                  if (match) publicQs[qi] = { ...q, sentiment_breakdown: match.sentiment_breakdown, themes: match.themes };
                }
              });
              data.public_questions = publicQs;
            }
            updateStep('ai_themes', 'done', 'AI text themes complete');
          })
          .catch(() => {
            updateStep('ai_themes', 'done', 'Text themes done (partial)');
          })
      );

      // Wait for ALL AI to finish
      await Promise.all(aiPromises);

      // Retry key-insights if Phase 1 returned null (may have been generating)
      if (!data._insights) {
        try {
          const retryRes = await api.get(`/projects/${projectId}/key-insights`);
          data._insights = retryRes?.data || null;
        } catch (_) { /* non-critical */ }
      }

      // Verify AI data completeness before snapshot
      const aiDriverCount = (data.driver_insights || []).filter(d => d.insight).length;
      const aiSegCount = (data._segmentAnalysis || []).length;
      console.log(`[Report] AI data check — insights: ${!!data._insights}, drivers w/AI: ${aiDriverCount}/${(data.driver_insights || []).length}, segAnalysis: ${aiSegCount}`);

      // Store the complete data
      reportDataRef.current = { ...data };
      setPreviewReportData({ ...data });

      // Save report snapshot to server (for PDF generation later)
      try {
        const token = localStorage.getItem('pulse_token');
        const API = process.env.REACT_APP_BACKEND_URL;
        const snapshotRes = await fetch(`${API}/api/projects/${currentProject?.id}/save-report-snapshot`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ report_data: { ...data }, project_info: currentProject }),
        });
        if (!snapshotRes.ok) throw new Error(`Snapshot save failed: ${snapshotRes.status}`);

        // Pre-generate PDF in the background — will be ready by the time user clicks Download
        pregenPdfTaskRef.current = null;
        fetch(`${API}/api/projects/${currentProject?.id}/generate-pdf-v2`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: '{}',
        }).then(res => res.ok ? res.json() : null)
          .then(d => { if (d?.task_id) pregenPdfTaskRef.current = d.task_id; })
          .catch(() => {}); // Silent — retry on download if needed
      } catch (snapErr) {
        console.warn('Failed to save report snapshot (PDF may use slow path):', snapErr);
      }

      setReportPhase('ready');

    } catch (err) {
      console.error('Report generation error:', err);
      setReportError(err.message || 'Failed to generate report');
      setReportPhase('confirm');
    }
  };

  // Download PDF — checks for pre-generated PDF first, falls back to on-demand generation
  const handleDownloadPDF = async () => {
    const data = reportDataRef.current;
    if (!data) return;

    setReportSteps(prev => {
      const filtered = prev.filter(s => s.id !== 'pdf');
      return [...filtered, { id: 'pdf', label: 'Preparing PDF...', status: 'active', detail: 'Checking for PDF...' }];
    });

    const token = localStorage.getItem('pulse_token');
    const API = process.env.REACT_APP_BACKEND_URL;
    const pid = currentProject?.id;

    try {
      let taskId = pregenPdfTaskRef.current;

      // Check if pre-generated PDF is already done
      if (taskId) {
        try {
          const statusRes = await fetch(`${API}/api/projects/${pid}/pdf-status/${taskId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (statusRes.ok) {
            const sd = await statusRes.json();
            if (sd.status === 'done') {
              // Instant download — PDF was pre-generated!
              setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, detail: 'Downloading...' } : s));
              await _downloadPdfFile(taskId, token, API, pid);
              setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, status: 'done', detail: 'PDF downloaded!' } : s));
              return;
            } else if (sd.status !== 'error') {
              // Still generating — poll from here
              await _pollAndDownload(taskId, token, API, pid);
              return;
            }
          }
        } catch (_) { /* fall through to on-demand */ }
        taskId = null;
      }

      // No pre-generated PDF ready — start on-demand generation
      setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, detail: 'Starting PDF generation...' } : s));
      await fetch(`${API}/api/projects/${pid}/save-report-snapshot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_data: data, project_info: currentProject }),
      });
      const startRes = await fetch(`${API}/api/projects/${pid}/generate-pdf-v2`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!startRes.ok) throw new Error(`Server returned ${startRes.status}`);
      const result = await startRes.json();
      await _pollAndDownload(result.task_id, token, API, pid);
    } catch (err) {
      console.error('PDF generation failed:', err.message);
      setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, status: 'error', detail: `Failed: ${err.message}` } : s));
    }
  };

  const _pollAndDownload = async (taskId, token, API, pid) => {
    const STATUS_MSGS = {
      starting: 'Starting PDF engine...',
      launching_browser: 'Launching renderer...',
      setting_auth: 'Authenticating...',
      loading_page: 'Loading report page...',
      waiting_for_render: 'Rendering slides...',
      rendering_pdf: 'Generating PDF file...',
      done: 'Downloading...',
    };
    let status = 'starting';
    let pollFailures = 0;
    while (status !== 'done' && status !== 'error') {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const pollRes = await fetch(`${API}/api/projects/${pid}/pdf-status/${taskId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if ([520, 502, 504].includes(pollRes.status)) {
          pollFailures++;
          if (pollFailures >= 20) throw new Error('Server temporarily unavailable');
          continue;
        }
        if (!pollRes.ok) throw new Error(`Status ${pollRes.status}`);
        const pollData = await pollRes.json();
        status = pollData.status;
        pollFailures = 0;
        setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, detail: STATUS_MSGS[status] || 'Processing...' } : s));
        if (status === 'error') throw new Error(pollData.error || 'PDF generation failed on server');
      } catch (pollErr) {
        if (pollErr.message?.includes('PDF generation failed')) throw pollErr;
        pollFailures++;
        if (pollFailures >= 20) throw new Error('Server temporarily unavailable');
      }
    }
    await _downloadPdfFile(taskId, token, API, pid);
    setReportSteps(prev => prev.map(s => s.id === 'pdf' ? { ...s, status: 'done', detail: 'PDF downloaded!' } : s));
  };

  const _downloadPdfFile = async (taskId, token, API, pid) => {
    const dlRes = await fetch(`${API}/api/projects/${pid}/download-pdf/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!dlRes.ok) throw new Error('Failed to download PDF');
    const blob = await dlRes.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    const projName = (currentProject?.name || 'Report').replace(/\s+/g, '_');
    a.download = `Pulse_${projName}_${today}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Open preview using already-generated data
  const handleViewPreview = () => {
    if (reportDataRef.current) {
      setPreviewReportData({ ...reportDataRef.current });
      setShowReportPreview(true);
      setShowReportModal(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6">
          <div className="flex items-center gap-3 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const { participation, segments_by_type, driver_summaries, question_summaries, key_findings, overall_sentiment } = summaryData || {};
  
  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in print:space-y-4">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Executive Summary
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            Key insights from {currentProject?.name || 'your project'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/project/${projectId}/dashboards/summary/configure`)}
              className="gap-2"
              size="sm"
            >
              <Settings className="w-4 h-4" />
              Configure
            </Button>
          )}
          <Button 
            onClick={() => { setReportPhase('confirm'); setReportSteps([]); setReportError(null); setShowReportModal(true); }}
            className="gap-2"
            size="sm"
            data-testid="export-report-btn"
          >
            <FileText className="w-4 h-4" />
            Generate Report
          </Button>
        </div>
      </div>
      
      {/* Overall Sentiment Hero */}
      <Card className="bg-gradient-to-br from-[#5AA2AE]/10 to-[#5AA2AE]/5 border-[#5AA2AE]/20">
        <CardContent className="py-4 md:py-6 px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
            <div>
              <p className="text-xs md:text-sm text-[#5AA2AE] font-medium mb-1">Overall Sentiment Score</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-bold text-slate-900">
                  {formatScore(overall_sentiment || 0, 10)}
                </span>
                <span className="text-lg md:text-xl text-slate-400">/10</span>
              </div>
              <p className="text-xs md:text-sm text-slate-500 mt-1 md:mt-2">
                {participation?.total_responses || 0} responses • {participation?.total_stakeholders || 0} stakeholders
              </p>
            </div>
            
            {/* Quick stats */}
            <div className="grid grid-cols-4 gap-2 md:gap-4">
              <div className="text-center p-2 md:p-3 bg-white/50 rounded-lg">
                <p className="text-lg md:text-2xl font-bold text-[#5AA2AE]">{participation?.invited || 0}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Invited</p>
              </div>
              <div className="text-center p-2 md:p-3 bg-white/50 rounded-lg">
                <p className="text-lg md:text-2xl font-bold text-purple-600">{participation?.reference || 0}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Ref</p>
              </div>
              <div className="text-center p-2 md:p-3 bg-white/50 rounded-lg">
                <p className="text-lg md:text-2xl font-bold text-amber-600">{participation?.pending || 0}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Pending</p>
              </div>
              <div className="text-center p-2 md:p-3 bg-white/50 rounded-lg">
                <p className="text-lg md:text-2xl font-bold text-slate-500">{participation?.public || participation?.anonymous || 0}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Public</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Key Metrics + Trend Analysis - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <KeyMetricsTile 
          topMetrics={keyInsights?.top_metrics}
          bottomMetrics={keyInsights?.bottom_metrics}
          loading={loadingInsights}
        />
        <TrendAnalysisChart
          projectId={projectId}
          segmentTypes={segmentTypes}
        />
      </div>
      
      {/* AI Insights Tile - Full Width */}
      <AIInsightsTile 
        insights={keyInsights?.insights}
        loading={loadingInsights}
        onRefresh={refreshInsights}
      />
      
      {/* Driver Scores */}
      {driver_summaries && driver_summaries.length > 0 && (
        <Card>
          <CardHeader className="px-4 md:px-6 py-3 md:py-4">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <BarChart3 className="w-4 md:w-5 h-4 md:h-5 text-[#5AA2AE]" />
              Driver Performance
              {selectedSegment && (
                <Badge variant="secondary" className="ml-2 font-normal text-xs">
                  {selectedSegment.segment_name}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              {selectedSegment 
                ? selectedSegmentMeetsThreshold
                  ? `Driver scores for ${selectedSegment.segment_name}`
                  : `${selectedSegment.segment_name} is below anonymity threshold`
                : 'Scores across key areas'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6">
            {selectedSegment && !selectedSegmentMeetsThreshold ? (
              <div className="text-center py-6">
                <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Data hidden to protect privacy</p>
              </div>
            ) : loadingDrivers ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-lg" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {(filteredDrivers || driver_summaries).map((driver, i) => (
                  <DriverScoreGauge key={i} driver={driver} />
                ))}
                {filteredDrivers && filteredDrivers.length === 0 && (
                  <div className="col-span-full text-center py-6 text-slate-400 text-sm">
                    No driver data available for this segment
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Stakeholder Breakdown by Segment - Collapsible */}
      {segments_by_type && segments_by_type.length > 0 && (
        <Collapsible open={stakeholderSectionOpen} onOpenChange={setStakeholderSectionOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-slate-50/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#5AA2AE]" />
                    <div>
                      <CardTitle className="text-base">Stakeholder Distribution</CardTitle>
                      <CardDescription className="text-xs">
                        {participation?.invited || 0} invited • {participation?.reference || 0} reference
                        {selectedSegment && (
                          <span className="ml-2 text-[#5AA2AE] font-medium">
                            • Filtering: {selectedSegment.segment_name}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSegment && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2 text-xs"
                        onClick={(e) => { e.stopPropagation(); setSelectedSegment(null); }}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear filter
                      </Button>
                    )}
                    {stakeholderSectionOpen ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {segments_by_type.filter(st => st.segments.length > 0).map((segmentType, i) => (
                    <SegmentBreakdownChart 
                      key={i} 
                      segmentType={segmentType}
                      onSegmentClick={handleSegmentClick}
                      selectedSegment={selectedSegment}
                    />
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
      
      {/* About Me Question Summary */}
      {question_summaries && question_summaries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#5AA2AE]" />
              Response Summary
              {selectedSegment && (
                <Badge variant="secondary" className="ml-2 font-normal">
                  {selectedSegment.segment_name}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {selectedSegment 
                ? selectedSegmentMeetsThreshold 
                  ? `Showing responses from ${selectedSegment.segment_name} stakeholders - click any tile for details`
                  : `${selectedSegment.segment_name} is below the anonymity threshold - data hidden to protect privacy`
                : 'Overview of About Me question responses - click any tile for details'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedSegment && !selectedSegmentMeetsThreshold ? (
              <div className="text-center py-8">
                <ShieldAlert className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Data Protected</p>
                <p className="text-sm text-slate-400 mt-1">
                  {selectedSegment.segment_name} has fewer than {currentProject?.anonymity_threshold || 3} responses.
                  <br />
                  Individual data is hidden to protect stakeholder privacy.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setSelectedSegment(null)}
                >
                  Clear filter to view all data
                </Button>
              </div>
            ) : (
              (() => {
              // Filter questions based on dashboard config
              const configuredQuestions = dashboardConfig?.response_summary_questions;
              let filteredQuestions = configuredQuestions && configuredQuestions.length > 0
                ? question_summaries.filter(q => configuredQuestions.includes(q.id) || configuredQuestions.includes(q.question_id))
                : question_summaries;
              
              // If a segment is selected, filter the by_segment data to show only that segment's scores
              if (selectedSegment) {
                filteredQuestions = filteredQuestions.map(q => {
                  if (q.question_type === 'slider' && q.by_segment) {
                    const segmentData = q.by_segment.find(s => s.segment_name === selectedSegment.segment_name);
                    if (segmentData) {
                      return {
                        ...q,
                        avg: segmentData.avg,
                        response_count: segmentData.response_count,
                        _filtered_by_segment: selectedSegment.segment_name
                      };
                    }
                  }
                  return q;
                }).filter(q => {
                  // Only show questions that have data for the selected segment
                  if (q.question_type === 'slider') {
                    return q._filtered_by_segment || !selectedSegment;
                  }
                  return true;
                });
              }
              
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredQuestions.slice(0, 9).map((q, i) => (
                      <QuestionSummaryCard 
                        key={i} 
                        question={q} 
                        onClick={setSelectedQuestion}
                      />
                    ))}
                  </div>
                  {filteredQuestions.length > 9 && (
                    <p className="text-sm text-slate-400 mt-4 text-center">
                      + {filteredQuestions.length - 9} more questions
                    </p>
                  )}
                  {filteredQuestions.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-8">
                      {selectedSegment 
                        ? `No data available for ${selectedSegment.segment_name}. Try selecting a different segment.`
                        : 'No questions configured for Response Summary.'
                      }
                      {isAdmin && !selectedSegment && (
                        <Button variant="link" className="p-0 h-auto ml-1" onClick={() => navigate(`/project/${projectId}/dashboards/summary/configure`)}>
                          Configure dashboard
                        </Button>
                      )}
                    </p>
                  )}
                </>
              );
            })()
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Question Detail Modal */}
      <Dialog open={!!selectedQuestion} onOpenChange={(open) => !open && setSelectedQuestion(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedQuestion?.heading || 'Question Details'}</DialogTitle>
            <DialogDescription>
              {selectedQuestion?.question_type} question • {selectedQuestion?.response_count} responses
            </DialogDescription>
          </DialogHeader>
          <QuestionDetailModal 
            question={selectedQuestion}
            segmentTypes={segmentTypes}
            onClose={() => setSelectedQuestion(null)}
          />
        </DialogContent>
      </Dialog>
      
      {/* Anonymity Notice */}
      <Card className="bg-slate-50 border-slate-200 print:hidden">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-[#5AA2AE]/10">
              <EyeOff className="w-4 h-4 text-[#5AA2AE]" />
            </div>
            <div>
              <p className="font-medium text-sm">Anonymity Protection Active</p>
              <p className="text-sm text-slate-500">
                All data shown meets the minimum threshold of 3 responses. 
                Individual responses cannot be identified.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Print-only report content */}
      {reportData && (
        <div className="hidden print:block space-y-8 pt-8 border-t-2">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold">{reportData.project_name}</h1>
            <p className="text-slate-500">Executive Report - Generated {new Date(reportData.generated_at).toLocaleDateString()}</p>
          </div>
          
          {/* Executive Summary */}
          <div className="page-break-after">
            <h2 className="text-xl font-bold border-b pb-2 mb-4">Executive Summary</h2>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap">
              {reportData.sections?.executive_summary}
            </div>
          </div>
          
          {/* AI Commentary */}
          <div className="page-break-after">
            <h2 className="text-xl font-bold border-b pb-2 mb-4">AI-Powered Analysis</h2>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap">
              {reportData.sections?.ai_commentary}
            </div>
          </div>
          
          {/* Driver Insights */}
          {reportData.sections?.driver_insights?.map((driver, i) => (
            <div key={i} className="page-break-inside-avoid mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
                {driver.driver} - {driver.score}/10
              </h3>
              <p className="text-sm text-slate-600 mb-2">{driver.count} responses</p>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap bg-slate-50 p-4 rounded">
                {driver.insight}
              </div>
            </div>
          ))}
          
          {/* Anonymous Feedback */}
          {reportData.sections?.anonymous_summary && (
            <div className="page-break-after">
              <h2 className="text-xl font-bold border-b pb-2 mb-4">Anonymous Feedback Summary</h2>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                {reportData.sections.anonymous_summary}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Report Generation Modal */}
      <Dialog open={showReportModal} onOpenChange={(open) => { if (!open && reportPhase !== 'generating') setShowReportModal(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="report-generation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#5AA2AE]" />
              {reportPhase === 'confirm' ? 'Generate Report' : reportPhase === 'generating' ? 'Generating Report...' : 'Report Ready'}
            </DialogTitle>
            <DialogDescription className="sr-only">Report generation progress</DialogDescription>
          </DialogHeader>

          {reportPhase === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                This will generate a comprehensive stakeholder intelligence report including AI-powered analysis. The process typically takes <strong>1-3 minutes</strong>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 space-y-1">
                <p className="font-medium">What will be generated:</p>
                <ul className="list-disc ml-4 space-y-0.5 text-amber-600">
                  <li>Executive summary with key insights</li>
                  <li>AI-driven driver analysis</li>
                  <li>Segment heatmaps with AI commentary</li>
                  <li>Trend analysis charts</li>
                </ul>
              </div>
              {reportError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                  {reportError}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowReportModal(false)}>Cancel</Button>
                <Button size="sm" className="bg-[#5AA2AE] hover:bg-[#4a8a94] gap-2" onClick={generateFullReport} data-testid="report-confirm-generate">
                  <Sparkles className="w-4 h-4" />
                  Generate Report
                </Button>
              </div>
            </div>
          )}

          {reportPhase === 'generating' && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Please wait while we collect data and generate AI analysis...</p>
              {reportSteps.map(step => (
                <div key={step.id} className="flex items-center gap-3" data-testid={`report-step-${step.id}`}>
                  {step.status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-slate-200 flex-shrink-0" />}
                  {step.status === 'active' && <Loader2 className="w-5 h-5 text-[#5AA2AE] animate-spin flex-shrink-0" />}
                  {step.status === 'done' && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                  {step.status === 'error' && <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                  <span className={`text-sm ${step.status === 'active' ? 'text-slate-700 font-medium' : step.status === 'done' ? 'text-slate-500' : 'text-slate-400'}`}>
                    {step.detail || step.label}
                  </span>
                </div>
              ))}
              <Progress value={Math.round((reportSteps.filter(s => s.status === 'done').length / Math.max(reportSteps.length, 1)) * 100)} className="h-1.5 mt-2" />
            </div>
          )}

          {reportPhase === 'ready' && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-emerald-700 font-medium">Report generated successfully!</p>
              </div>
              <p className="text-xs text-slate-500">Choose how you want to view the report:</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 gap-2" onClick={handleViewPreview} data-testid="report-view-preview">
                  <Eye className="w-4 h-4" />
                  View on Screen
                </Button>
                <Button className="flex-1 gap-2 bg-[#5AA2AE] hover:bg-[#4a8a94]" onClick={handleDownloadPDF} data-testid="report-download-pdf-btn">
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
              {reportSteps.find(s => s.id === 'pdf') && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {reportSteps.find(s => s.id === 'pdf')?.status === 'active' && <Loader2 className="w-3 h-3 animate-spin" />}
                  {reportSteps.find(s => s.id === 'pdf')?.status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                  {reportSteps.find(s => s.id === 'pdf')?.status === 'error' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                  <span className={reportSteps.find(s => s.id === 'pdf')?.status === 'error' ? 'text-red-600' : ''}>
                    {reportSteps.find(s => s.id === 'pdf')?.detail}
                  </span>
                  {reportSteps.find(s => s.id === 'pdf')?.status === 'error' && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-[#5AA2AE]" onClick={handleDownloadPDF}>
                      Retry
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Preview Carousel */}
      {showReportPreview && previewReportData && (
        <ReportPreview
          data={previewReportData}
          project={currentProject}
          onClose={() => setShowReportPreview(false)}
          pregenTaskId={pregenPdfTaskRef.current}
        />
      )}
    </div>
  );
}
